import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  Alert,
  Typography,
  Stack,
  useTheme,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { Autocomplete, TextField, Grid, Button } from "@/core/ui";

import { Copyright } from "./components/BasePublic";

import BackgroundPadel from "../../images/fundoPadel.png";
import LogoIconeLight from "../../images/LogoIconeLight.png";
import LogoIconeDark from "../../images/LogoIconeDark.png";
import LogoApeak from "../../images/apeak_club.png";
import useRegister from "./hooks/useRegister";
import { formatMobilePhoneWithDDD } from "./utils/phone";

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();

  const {
    initialize,
    handleCreateUser,
    categoriesData,
    validationError,
    requestError,
    successMessage,
    actionLoading,
    handleChange,
    formData,
  } = useRegister();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const logoSrc = LogoApeak;
  const registerTheme = theme.custom?.login ?? {};

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `linear-gradient(${registerTheme.overlay ?? "rgba(0, 0, 0, 0.55)"}, ${registerTheme.overlay ?? "rgba(0, 0, 0, 0.55)"}), url(${BackgroundPadel})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          borderRadius: 5,
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          border: `1px solid ${registerTheme.cardBorder ?? "rgba(255, 255, 255, 0.3)"}`,
          backdropFilter: "blur(5px)",
          bgcolor: registerTheme.cardBackground ?? "rgba(255, 255, 255, 0.88)",
          boxShadow:
            registerTheme.shadow ?? "0 22px 55px rgba(5, 15, 28, 0.35)",
        }}
      >
        <Stack spacing={1.2} alignItems="center" mb={2}>
          <Box
            component="img"
            src={logoSrc}
            alt="Logo Padel Rank"
            sx={{
              width: { xs: 120, sm: 180 },
              height: { xs: 120, sm: 180 },
              objectFit: "contain",
            }}
          />

          <Typography
            component="h1"
            variant="h4"
            fontWeight={800}
            sx={{
              letterSpacing: 0.8,
              color: registerTheme.title ?? "primary.main",
              textAlign: "center",
            }}
          >
            Criar Conta
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: registerTheme.subtitle ?? "text.secondary",
              textAlign: "center",
              mb: 1,
            }}
          >
            Preencha os dados para se cadastrar
          </Typography>
        </Stack>

        <Box component="form">
          <Grid desktop={6}>
            <TextField
              label="Nome completo"
              value={formData.name || ""}
              error={validationError?.field === "name"}
              onChange={handleChange("name")}
            />
          </Grid>

          <Grid desktop={6}>
            <TextField
              label="Email"
              value={formData.email || ""}
              error={validationError?.field === "email"}
              onChange={handleChange("email")}
            />
          </Grid>

          <Grid desktop={6}>
            <TextField
              label="Nascimento"
              value={formData.birthday || ""}
              error={validationError?.field === "birthday"}
              type="date"
              onChange={handleChange("birthday")}
            />
          </Grid>

          <Grid desktop={6}>
            <Autocomplete
              label="Categoria"
              options={categoriesData}
              value={formData.category || null}
              getOptionLabel={(option) => `${option.name} - ${option.type}`}
              onChange={handleChange("category", { type: "autocomplete" })}
              error={validationError?.field === "category"}
              helperText={validationError?.category}
            />
          </Grid>

          <Grid desktop={6}>
            <TextField
              label="Telefone"
              value={formData.phone || ""}
              error={validationError?.field === "phone"}
              onChange={handleChange("phone", {
                transform: formatMobilePhoneWithDDD,
              })}
              placeholder="(XX) 9XXXX-XXXX"
              inputProps={{ maxLength: 15 }}
            />
          </Grid>

          <Grid desktop={6}>
            <TextField
              label="Senha"
              type={showPassword ? "text" : "password"}
              value={formData.password || ""}
              error={validationError?.field === "password"}
              onChange={handleChange("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(event) => event.preventDefault()}
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {formData.password ? (
            <Grid desktop={6}>
              <Stack spacing={0.4} sx={{ pl: 0.5 }}>
                {[
                  {
                    label: "Mais de 6 caracteres",
                    ok: formData.password.length > 6,
                  },
                  {
                    label: "Pelo menos uma letra minúscula",
                    ok: /[a-z]/.test(formData.password),
                  },
                  {
                    label: "Pelo menos uma letra maiúscula",
                    ok: /[A-Z]/.test(formData.password),
                  },
                  {
                    label: "Pelo menos um número",
                    ok: /\d/.test(formData.password),
                  },
                ].map(({ label, ok }) => (
                  <Stack
                    key={label}
                    direction="row"
                    spacing={0.6}
                    alignItems="center"
                  >
                    {ok ? (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 14, color: "success.main" }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ fontSize: 14, color: "text.disabled" }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color={ok ? "success.main" : "text.disabled"}
                    >
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          ) : null}

          <Grid desktop={6}>
            <TextField
              label="Confirmar senha"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword || ""}
              error={validationError?.field === "confirmPassword"}
              onChange={handleChange("confirmPassword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={(event) => event.preventDefault()}
                      aria-label={
                        showConfirmPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {validationError?.error ? (
            <Grid desktop={6}>
              <Alert
                severity="error"
                variant="filled"
                sx={{
                  mt: 0.5,
                  borderRadius: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontWeight: 600,
                  py: 0.6,
                  "& .MuiAlert-icon": {
                    mr: 1,
                    alignSelf: "center",
                  },
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                {validationError.error}
              </Alert>
            </Grid>
          ) : null}

          {requestError ? (
            <Grid desktop={6}>
              <Alert
                severity="error"
                variant="filled"
                sx={{
                  mt: 0.5,
                  borderRadius: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontWeight: 600,
                  py: 0.6,
                  "& .MuiAlert-icon": {
                    mr: 1,
                    alignSelf: "center",
                  },
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                {requestError}
              </Alert>
            </Grid>
          ) : null}

          {successMessage ? (
            <Grid desktop={6}>
              <Alert
                severity="success"
                variant="filled"
                sx={{
                  mt: 0.5,
                  borderRadius: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontWeight: 600,
                  py: 0.6,
                  "& .MuiAlert-icon": {
                    mr: 1,
                    alignSelf: "center",
                  },
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
              >
                {successMessage}
              </Alert>
            </Grid>
          ) : null}

          <Grid desktop={6}>
            <Button
              fullWidth
              variant="contained"
              disabled={actionLoading}
              onClick={() => {
                handleCreateUser(formData);
              }}
              sx={{
                mt: 1,
                mb: 1.2,
                py: 1.2,
                borderRadius: 2.5,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: registerTheme.buttonText ?? "#f7fbff",
                bgcolor: "primary.main",
                boxShadow: "0 10px 30px rgba(24, 49, 83, 0.35)",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              {actionLoading ? "Criando conta..." : "Criar conta"}
            </Button>
          </Grid>

          <Button
            component={RouterLink}
            to="/login"
            fullWidth
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              py: 1.1,
              fontWeight: 700,
            }}
          >
            Voltar para login
          </Button>
        </Box>

        <Copyright sx={{ mt: 1.5 }} />
      </Box>
    </Box>
  );
}
